<template>
  <div v-if="pa && pa.activeSku" class="sku-spec-sections sku-detail-body">
    <div class="sku-section">
      <div class="section-title">销售信息 <small>销售规格与基础属性</small></div>
      <div class="sku-grid">
        <div class="sku-field">
          <div class="sku-label">SKU编码</div>
          <a-form-model-item :colon="false" :label-col="fieldLabelCol" :wrapper-col="fieldWrapperCol" class="sku-field-control">
            <a-input v-model="pa.activeSku.skuCode" placeholder="系统保存后自动生成，支持手动修改" />
          </a-form-model-item>
        </div>
        <div class="sku-field">
          <div class="sku-label req">规格名称</div>
          <a-form-model-item :colon="false" :label-col="fieldLabelCol" :wrapper-col="fieldWrapperCol" class="sku-field-control">
            <a-input v-model="pa.activeSku.skuName" placeholder="请输入规格名称" />
          </a-form-model-item>
        </div>

        <div class="sku-unit-block">
          <div class="section-title section-title-sub">单位配置 <small>新建规格与左侧商品基本单位同步；已保存规格以本页基本单位为准。销售选整箱时，整箱单位须与基本单位不同并填写箱规</small></div>
          <div class="sku-grid sku-grid-nested">
            <div class="sku-field">
              <div class="sku-label req">基本单位</div>
              <a-form-model-item :colon="false" :label-col="fieldLabelCol" :wrapper-col="fieldWrapperCol" class="sku-field-control">
                <a-select
                  :key="'sku-base-unit-' + pa.activeSkuIndex + '-' + pa.skuBaseUnitSelectValue(pa.activeSku)"
                  :value="pa.skuBaseUnitSelectValue(pa.activeSku)"
                  placeholder="请选择"
                  @change="v => pa.onConfigBaseChange(pa.activeSku, v)">
                  <a-select-option v-for="o in pa.unitOptions" :key="o" :value="o">{{ o }}</a-select-option>
                </a-select>
              </a-form-model-item>
            </div>
            <div class="sku-field">
              <div class="sku-label" :class="{ req: pa.activeSku.saleUnitMode === 'case' }">整箱单位</div>
              <a-form-model-item :colon="false" :label-col="fieldLabelCol" :wrapper-col="fieldWrapperCol" class="sku-field-control">
                <a-select
                  v-model="pa.activeSku.caseUnitName"
                  placeholder="默认与基本单位相同"
                  @change="v => pa.onCaseUnitChange(pa.activeSku, v)">
                  <a-select-option v-for="o in pa.unitOptions" :key="'case-' + o" :value="o">{{ o }}</a-select-option>
                </a-select>
              </a-form-model-item>
            </div>
            <div class="sku-field">
              <div class="sku-label req">销售单位</div>
              <a-form-model-item :colon="false" :label-col="fieldLabelCol" :wrapper-col="fieldWrapperCol" class="sku-field-control">
                <a-radio-group :value="pa.activeSku.saleUnitMode || 'base'" @change="e => pa.onSaleModeChange(pa.activeSku, e)">
                  <a-radio value="base">基本单位</a-radio>
                  <a-radio value="case">整箱单位</a-radio>
                </a-radio-group>
              </a-form-model-item>
            </div>
            <div class="sku-field sku-field-top">
              <div class="sku-label" :class="{ req: pa.activeSku.saleUnitMode === 'case' }">箱规</div>
              <a-form-model-item :colon="false" :label-col="fieldLabelCol" :wrapper-col="fieldWrapperCol" class="sku-field-control">
                <a-input-number
                  v-model="pa.activeSku.caseRate"
                  :min="0.000001"
                  :precision="6"
                  class="sku-input-full"
                  placeholder="默认 1" />
                <div class="help">1 整箱单位 = 箱规 × 基本单位。默认 1，即 1:1。</div>
              </a-form-model-item>
            </div>
          </div>
        </div>

        <div class="sku-field">
          <div class="sku-label req">最小下单数</div>
          <a-form-model-item :colon="false" :label-col="fieldLabelCol" :wrapper-col="fieldWrapperCol" class="sku-field-control">
            <div class="input-group">
              <a-input-number v-model="pa.activeSku.minQty" :min="0" class="input-group-main" />
              <span class="suffix">{{ pa.skuSaleUnitLabel || '—' }}</span>
            </div>
          </a-form-model-item>
        </div>
        <div class="sku-field">
          <div class="sku-label">毛重(kg)</div>
          <a-form-model-item :colon="false" :label-col="fieldLabelCol" :wrapper-col="fieldWrapperCol" class="sku-field-control">
            <a-input-number v-model="pa.activeSku.grossWeight" :min="0" :precision="3" class="sku-input-full" placeholder="请输入毛重" />
          </a-form-model-item>
        </div>
        <div class="sku-field">
          <div class="sku-label">净重(kg)</div>
          <a-form-model-item :colon="false" :label-col="fieldLabelCol" :wrapper-col="fieldWrapperCol" class="sku-field-control">
            <a-input-number v-model="pa.activeSku.netWeight" :min="0" :precision="3" class="sku-input-full" placeholder="请输入净重" />
          </a-form-model-item>
        </div>
        <div class="sku-field">
          <div class="sku-label">体积(m³)</div>
          <a-form-model-item :colon="false" :label-col="fieldLabelCol" :wrapper-col="fieldWrapperCol" class="sku-field-control">
            <a-input-number v-model="pa.activeSku.volume" :min="0" :precision="3" class="sku-input-full" placeholder="请输入体积" />
          </a-form-model-item>
        </div>
        <div class="sku-field">
          <div class="sku-label">损耗比例</div>
          <a-form-model-item :colon="false" :label-col="fieldLabelCol" :wrapper-col="fieldWrapperCol" class="sku-field-control">
            <div class="input-group">
              <a-input-number v-model="pa.activeSku.lossRate" :min="0" :precision="1" class="input-group-main" />
              <span class="suffix">%</span>
            </div>
          </a-form-model-item>
        </div>
        <div class="sku-field">
          <div class="sku-label">是否称重</div>
          <a-form-model-item :colon="false" :label-col="fieldLabelCol" :wrapper-col="fieldWrapperCol" class="sku-field-control">
            <a-radio-group v-model="pa.activeSku.weigh">
              <a-radio value="是">是</a-radio>
              <a-radio value="否">否</a-radio>
            </a-radio-group>
          </a-form-model-item>
        </div>
      </div>
    </div>

    <div class="sku-section">
      <div class="section-title">更多设置 <small>规格编码、状态、区域、图片及商品描述</small></div>
      <div class="sku-grid">
        <div class="sku-field">
          <div class="sku-label">分拣分包</div>
          <a-form-model-item :colon="false" :label-col="fieldLabelCol" :wrapper-col="fieldWrapperCol" class="sku-field-control">
            <a-switch
              :checked="pa.activeSku.sorting === '开启'"
              checked-children="开"
              un-checked-children="关"
              @change="v => { pa.activeSku.sorting = v ? '开启' : '关闭' }"
            />
          </a-form-model-item>
        </div>
        <div class="sku-field">
          <div class="sku-label">区域</div>
          <a-form-model-item :colon="false" :label-col="fieldLabelCol" :wrapper-col="fieldWrapperCol" class="sku-field-control">
            <a-input v-model="pa.activeSku.region" placeholder="请输入区域" />
          </a-form-model-item>
        </div>
        <div class="sku-field sku-field-top">
          <div class="sku-label">商品图片</div>
          <a-form-model-item :colon="false" :label-col="fieldLabelCol" :wrapper-col="fieldWrapperCol" class="sku-field-control">
            <product-attachment-upload v-model="pa.activeSku.image" kind="image" />
            <div class="help">默认继承商品主图，规格可单独维护。</div>
          </a-form-model-item>
        </div>
        <div class="sku-field">
          <div class="sku-label">产地</div>
          <a-form-model-item :colon="false" :label-col="fieldLabelCol" :wrapper-col="fieldWrapperCol" class="sku-field-control">
            <a-input v-model="pa.activeSku.origin" placeholder="请输入产地" />
          </a-form-model-item>
        </div>
        <div class="sku-field sku-field-top">
          <div class="sku-label">描述</div>
          <a-form-model-item :colon="false" :label-col="fieldLabelCol" :wrapper-col="fieldWrapperCol" class="sku-field-control">
            <a-textarea v-model="pa.activeSku.skuDesc" :rows="2" placeholder="规格描述" />
          </a-form-model-item>
        </div>
        <div class="sku-field sku-field-top">
          <div class="sku-label">商品规格</div>
          <a-form-model-item :colon="false" :label-col="fieldLabelCol" :wrapper-col="fieldWrapperCol" class="sku-field-control">
            <a-textarea v-model="pa.activeSku.specDesc" :rows="2" placeholder="如：1斤/斤（约500g~600g/斤）" />
          </a-form-model-item>
        </div>
        <div class="sku-field sku-field-top">
          <div class="sku-label">商品特征</div>
          <a-form-model-item :colon="false" :label-col="fieldLabelCol" :wrapper-col="fieldWrapperCol" class="sku-field-control">
            <a-textarea v-model="pa.activeSku.feature" :rows="2" placeholder="规格级商品特征" />
          </a-form-model-item>
        </div>
        <div class="sku-field sku-field-top">
          <div class="sku-label">售后标准</div>
          <a-form-model-item :colon="false" :label-col="fieldLabelCol" :wrapper-col="fieldWrapperCol" class="sku-field-control">
            <a-textarea v-model="pa.activeSku.afterSale" :rows="2" placeholder="规格级售后标准" />
          </a-form-model-item>
        </div>
      </div>
    </div>

    <div class="sku-section">
      <div class="section-title">供应链信息 <small>包装、采购说明与库存规则</small></div>
      <div class="sku-grid">
        <div class="sku-field">
          <div class="sku-label">装箱类型</div>
          <a-form-model-item :colon="false" :label-col="fieldLabelCol" :wrapper-col="fieldWrapperCol" class="sku-field-control">
            <a-select v-model="pa.activeSku.packType" placeholder="请选择">
              <a-select-option v-for="item in pa.packOptions" :key="item" :value="item">{{ item }}</a-select-option>
            </a-select>
          </a-form-model-item>
        </div>
        <div class="sku-field sku-field-top sku-field-full">
          <div class="sku-label">采购描述</div>
          <a-form-model-item :colon="false" :label-col="fieldLabelCol" :wrapper-col="fieldWrapperCol" class="sku-field-control">
            <a-textarea v-model="pa.activeSku.purchaseDesc" :rows="2" placeholder="请输入采购说明" />
          </a-form-model-item>
        </div>
        <div class="sku-field sku-field-top">
          <div class="sku-label req">库存设置</div>
          <a-form-model-item :colon="false" :label-col="fieldLabelCol" :wrapper-col="fieldWrapperCol" class="sku-field-control">
            <div class="stock-box">
              <a-radio-group v-model="pa.activeSku.stockMode">
                <a-radio value="虚拟库存">虚拟库存</a-radio>
                <a-radio value="实际库存">实际库存</a-radio>
                <a-radio value="设置库存">设置库存</a-radio>
              </a-radio-group>
              <div v-if="pa.activeSku.stockMode === '设置库存'" class="stock-value-row">
                <div class="input-group">
                  <a-input-number v-model="pa.activeSku.stockValue" :min="0" class="input-group-main" />
                  <span class="suffix">{{ pa.skuSaleUnitLabel }}</span>
                </div>
              </div>
            </div>
          </a-form-model-item>
        </div>
      </div>
    </div>

    <div class="sku-section">
      <div class="section-title">备注 <small>规格级补充说明</small></div>
      <div class="sku-grid">
        <div class="sku-field sku-field-top sku-field-full">
          <div class="sku-label">备注</div>
          <a-form-model-item :colon="false" :label-col="fieldLabelCol" :wrapper-col="fieldWrapperCol" class="sku-field-control">
            <a-textarea v-model="pa.activeSku.remark" :rows="4" placeholder="请输入该规格的补充说明或业务备注" />
          </a-form-model-item>
        </div>
      </div>
    </div>

    <section class="section base-info-group">
      <div class="section-head base-info-group-head">
        <div class="section-title">仓库信息 <small>按当前规格维护采购属性、采购方式、供应商及供应成本</small></div>
        <a-button :disabled="!pa.canAddWarehouse" :title="pa.warehouseAddHint" @click="pa.addWarehouse">+ 添加仓库</a-button>
      </div>
      <div class="base-info-card">
        <component
          :is="WarehouseTable"
          v-if="WarehouseTable"
          :rows="pa.form.warehouses"
          :warehouse-options="pa.warehouseRecords"
          :supplier-options="pa.supplierRecords"
          @remove="pa.removeWarehouse" />
      </div>
    </section>
    <section class="section base-info-group">
      <div class="section-head base-info-group-head">
        <div class="section-title">商品MOQ设置 <small>按当前规格维护最低采购限制，可按数量或金额控制</small></div>
        <a-button @click="pa.openMoqModal">+ 添加MOQ规则</a-button>
      </div>
      <div class="base-info-card">
        <component :is="MoqTable" v-if="MoqTable" :rows="pa.form.moqs" @remove="pa.removeMoq" />
      </div>
    </section>
  </div>
</template>

<script>
import ProductAttachmentUpload from './ProductAttachmentUpload.vue'

export default {
  name: 'ProductSkuSpecSections',
  components: { ProductAttachmentUpload },
  inject: {
    pa: { from: 'productArchiveVm', default: null },
    WarehouseTable: { default: null },
    MoqTable: { default: null }
  },
  data () {
    return {
      fieldLabelCol: { span: 0 },
      fieldWrapperCol: { span: 24 }
    }
  }
}
</script>

<style scoped>
.sku-spec-sections.sku-detail-body {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  background: #f4f6f9;
  padding: 12px;
}

.sku-detail-body > .sku-section,
.sku-detail-body > .section.base-info-group {
  background: #fff;
  border: 1px solid #e3e8ef;
  border-radius: 6px;
  margin: 0 0 12px;
  padding: 18px 18px 20px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.02);
}
.sku-detail-body > .sku-section:last-child,
.sku-detail-body > .section.base-info-group:last-child {
  margin-bottom: 0;
}

.section-title {
  font-size: 15px;
  font-weight: 700;
  color: #26364d;
  padding-left: 10px;
  border-left: 3px solid #1677ff;
  line-height: 20px;
}
.section-title small {
  font-weight: 400;
  color: #98a2b3;
  font-size: 12px;
  margin-left: 8px;
}
.section-title-sub {
  grid-column: 1 / -1;
  margin: 4px 0 2px;
  border-left-width: 3px;
  font-size: 14px;
}

.sku-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(360px, 1fr));
  column-gap: 42px;
  row-gap: 16px;
  align-items: start;
  margin-top: 15px;
}
.sku-grid-nested {
  margin-top: 12px;
}
.sku-unit-block {
  grid-column: 1 / -1;
  border: 1px solid #dfe6ef;
  border-radius: 6px;
  background: #fbfcfe;
  padding: 14px 16px 16px;
  box-sizing: border-box;
}

.sku-field {
  display: grid;
  grid-template-columns: 126px minmax(0, 1fr);
  align-items: center;
  min-height: 34px;
  min-width: 0;
}
.sku-field-top {
  align-items: start;
}
.sku-field-top .sku-label {
  line-height: 34px;
}
.sku-field-full {
  grid-column: 1 / -1;
}

.sku-label {
  text-align: right;
  padding-right: 14px;
  color: #4b5a70;
  font-size: 13px;
  line-height: 34px;
  white-space: nowrap;
}
.sku-label.req::before {
  content: '*';
  color: #ff4d4f;
  margin-right: 3px;
}

.sku-field-control {
  margin-bottom: 0 !important;
  min-width: 0;
}
.sku-field-control >>> .ant-form-item-control {
  line-height: 1.4;
}

.sku-input-full {
  width: 100% !important;
}

.input-group {
  display: flex;
  align-items: stretch;
  max-width: 100%;
}
.input-group-main {
  flex: 1 1 auto;
  min-width: 0 !important;
  width: auto !important;
}
.input-group >>> .ant-input-number {
  width: 100% !important;
  border-radius: 5px 0 0 5px;
}
.input-group >>> .ant-input-number-input {
  border-radius: 5px 0 0 5px;
}
.suffix {
  display: flex;
  align-items: center;
  padding: 0 10px;
  border: 1px solid #d6dde8;
  border-left: 0;
  background: #f7f8fa;
  border-radius: 0 5px 5px 0;
  white-space: nowrap;
  color: #637085;
  font-size: 13px;
  line-height: 32px;
}

.help {
  font-size: 12px;
  color: #9ba5b5;
  margin-top: 5px;
  line-height: 1.55;
}

.stock-box {
  border: 1px solid #e3e8ef;
  border-radius: 6px;
  padding: 12px;
  background: #fbfcfe;
  width: 100%;
}
.stock-value-row {
  margin-top: 9px;
}

.sku-spec-sections >>> .ant-input,
.sku-spec-sections >>> .ant-select-selection,
.sku-spec-sections >>> .ant-input-number,
.sku-spec-sections >>> .ant-input-number-input {
  min-height: 34px;
  border-color: #d6dde8;
  border-radius: 5px;
}
.sku-spec-sections >>> .ant-select {
  width: 100%;
}
.sku-spec-sections >>> textarea.ant-input {
  min-height: 72px;
  line-height: 1.5;
  padding-top: 6px;
}

.sku-detail-body > .section.base-info-group .section-head {
  margin-bottom: 14px;
  padding-bottom: 12px;
}
.sku-detail-body > .section.base-info-group .section-title:before {
  display: none;
}
.sku-detail-body > .section.base-info-group .section-title {
  border-left: 3px solid #1677ff;
  padding-left: 10px;
  font-size: 15px;
  font-weight: 700;
  color: #26364d;
  gap: 0;
}
.sku-detail-body > .section.base-info-group .section-title small {
  margin-left: 8px;
  color: #98a2b3;
}

.sku-field-control >>> .ant-form-item-label {
  display: none !important;
  width: 0 !important;
  padding: 0 !important;
}
.sku-field-control >>> .ant-form-item-control-wrapper,
.sku-field-control >>> .ant-form-item-control {
  width: 100%;
  max-width: 100%;
}
.sku-field-control >>> .ant-col {
  width: 100%;
  max-width: 100%;
  flex: 0 0 100%;
}

@media (max-width: 1280px) {
  .sku-grid {
    grid-template-columns: 1fr;
    column-gap: 0;
  }
}

@media (max-width: 1100px) {
  .sku-grid {
    grid-template-columns: 1fr;
    column-gap: 0;
  }
  .sku-field {
    grid-template-columns: 112px minmax(0, 1fr);
  }
}
</style>
